// Briques partagees par les frises d'orchestration (environnement et serveur) : filtre d'heures,
// axe (jours + heures) colle en haut, grille quadrillee, barres rognees, ouverture des logs.
import moment from "moment";
import React, { useCallback, useContext, useState } from "react";
import { ReactReduxContext } from "react-redux";
import { DatePicker, Select, Tag } from "antd";
import frFR from "antd/lib/date-picker/locale/fr_FR";

import { Puces, useUrlParams } from "../utils/PageEntete";
import { setSelectedBuild, setShowLogsModal } from "../../redux/actions/logsActions";
import {
  JOURS_DEFAUT,
  JOURS_PERIODE,
  MAX_CYCLES,
  aujourdhui,
  decalerPlage,
  ecartJours,
  lirePeriode,
  validerPlage,
} from "./periode";
import { PRESETS_PLAGE, clePlage, graduations, normaliserPlage, pasGraduation } from "./frise";

// Locale francaise de moment disponible pour le calendrier, SANS devenir la locale globale
// (l'import d'une locale moment l'active : on restaure aussitot la locale d'origine).
const localeGlobale = moment.locale();
require("moment/locale/fr");
moment.locale(localeGlobale);
const LOCALE_PICKER = frFR.default || frFR;

export const GOUTTIERE = 230;
export const AXE_H = 34;
export const COULEURS_STATUT = { ok: "#2e9e6b", warning: "#ff8801", error: "#e5484d", running: "#3b82f6", unknown: "#b8c0cc" };
export const LIBELLES_STATUT = { ok: "OK", warning: "Warning", error: "Erreur", running: "En cours", unknown: "Inconnu" };
export const TAGS_STATUT = { ok: "green", warning: "orange", error: "red", running: "blue", unknown: "default" };
// Run "en cours" depuis plus de 24 h (stale) : affiche comme un run en cours, mais signale
// (contour orange, libelle, tag) et exclu du calcul de simultaneite.
export const libelleStale = (r) => `⚠ en cours depuis ${Math.max(24, Math.round(Number(r && r.duration_s) / 3600) || 24)} h`;
// « ◂ depuis le 26/10 » si le run a commence avant le debut de la frise.
export const libelleDepuis = (startMs, debutMs) => {
  if (isNaN(startMs) || isNaN(debutMs) || startMs >= debutMs) return null;
  const d = new Date(startMs);
  return `◂ depuis le ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
};
// « jusqu'au 30/10 ▸ » si le run se termine apres la fin de la frise.
export const libelleJusqua = (endMs, finMs) => {
  if (isNaN(endMs) || isNaN(finMs) || endMs <= finMs) return null;
  const d = new Date(endMs);
  return `jusqu'au ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ▸`;
};
export const nbStale = (runs) => (Array.isArray(runs) ? runs.filter((r) => r && r.stale).length : 0);
export const libelleStaleNote = (k) => `${k} ${k > 1 ? "jobs" : "job"} en cours depuis plus de 24 h`;

// Statut d'un run en tableau : tag du statut + tag orange « depuis > 24 h » pour un run stale.
export function StatutRun({ run }) {
  const k = LIBELLES_STATUT[run.status] ? run.status : "unknown";
  return (
    <>
      <Tag color={TAGS_STATUT[k]}>{LIBELLES_STATUT[k]}</Tag>
      {run.stale && (
        <Tag color="orange" title="Bloqué ?">
          depuis &gt; 24 h
        </Tag>
      )}
    </>
  );
}

const OPTIONS_HEURES = Array.from({ length: 25 }, (_, h) => ({ value: h, label: `${h} h` }));
const FORMAT_JOUR = "YYYY-MM-DD";

// Periode lue / ecrite dans l'URL : ?days=1|3|7 ou ?from=YYYY-MM-DD&to=YYYY-MM-DD (cycles).
export function usePeriode() {
  const [params, update] = useUrlParams();
  return [
    lirePeriode(params),
    {
      setDays: (n) => update({ days: n, from: null, to: null }),
      setRange: (from, to) => update({ from, to, days: null }),
    },
  ];
}

// Bloc de la barre de filtres : petit encadre, libelle en petites majuscules.
function Bloc({ titre, children }) {
  return (
    <div className="orch-bloc">
      <span className="orch-bloc-titre">{titre}</span>
      <div className="orch-bloc-corps">{children}</div>
    </div>
  );
}

// Periode : pilules 1/3/7 j OU selecteur de cycles (de D1 19 h a D2 19 h), exclusifs.
function SelecteurPeriode({ periode, onDays, onRange, oldest }) {
  const [choix, setChoix] = useState(null); // 1re date choisie, le temps de la selection
  const [erreur, setErreur] = useState(null);
  const today = aujourdhui();
  const range = periode.mode === "range";
  const disabledDate = (m) => {
    const j = m.format(FORMAT_JOUR);
    if (j > today) return true;
    if (oldest && j < oldest) return true;
    if (choix) return j <= choix || ecartJours(choix, j) > MAX_CYCLES;
    return false;
  };
  const decale = (n) => (range ? decalerPlage(periode.from, periode.to, n) : null);
  const bloque = (n) => {
    const d = decale(n);
    return !d || validerPlage(d.from, d.to, { oldest, today }) !== null;
  };
  const fleche = (n, libelle, symbole) => (
    <button
      type="button"
      className="orch-fleche"
      aria-label={libelle}
      title={libelle}
      disabled={bloque(n)}
      onClick={() => {
        const d = decale(n);
        onRange(d.from, d.to);
      }}
    >
      {symbole}
    </button>
  );
  return (
    <>
      <Puces
        inline
        label="Période"
        items={JOURS_PERIODE.map((j) => ({ key: String(j), label: `${j} j` }))}
        value={range ? "" : String(periode.days)}
        onChange={(k) => {
          setErreur(null);
          onDays(Number(k));
        }}
      />
      <span className="orch-periode-plage">
        {fleche(-1, "Cycle précédent", "◂")}
        <DatePicker.RangePicker
          size="small"
          locale={LOCALE_PICKER}
          format="DD/MM/YYYY"
          placeholder={["Début (19 h)", "Fin (19 h)"]}
          allowClear
          disabledDate={disabledDate}
          value={range ? [moment(periode.from, FORMAT_JOUR), moment(periode.to, FORMAT_JOUR)] : null}
          onCalendarChange={(vals) => setChoix(vals && vals[0] && !vals[1] ? vals[0].format(FORMAT_JOUR) : null)}
          onChange={(vals) => {
            setChoix(null);
            if (!vals || !vals[0] || !vals[1]) {
              setErreur(null);
              onDays(JOURS_DEFAUT);
              return;
            }
            const d1 = vals[0].format(FORMAT_JOUR);
            const d2 = vals[1].format(FORMAT_JOUR);
            const err = validerPlage(d1, d2, { oldest, today });
            setErreur(err);
            if (!err) onRange(d1, d2);
          }}
          style={{ width: 230 }}
        />
        {fleche(1, "Cycle suivant", "▸")}
        {erreur && (
          <span className="orch-periode-erreur" role="alert">
            {erreur}
          </span>
        )}
      </span>
    </>
  );
}

// Barre de filtres commune aux deux frises, sur une seule ligne : Periode | Cycle | Heures.
// Le filtre d'heures est purement local (aucun appel d'API).
export function BarreFiltres({ periode, onDays, onRange, oldest, plage, onPlage }) {
  const p = normaliserPlage(plage);
  return (
    <div className="orch-filtres" data-testid="barre-filtres">
      <Bloc titre="Période">
        <SelecteurPeriode periode={periode} onDays={onDays} onRange={onRange} oldest={oldest} />
      </Bloc>
      <Bloc titre="Cycle">
        <Puces
          inline
          label="Cycle"
          items={PRESETS_PLAGE.map((x) => ({ key: x.key, label: x.label }))}
          value={clePlage(p)}
          onChange={(k) => onPlage(PRESETS_PLAGE.find((x) => x.key === k).plage)}
        />
      </Bloc>
      <Bloc titre="Heures">
        <span className="orch-plage" role="group" aria-label="Heures">
          de
          <Select
            size="small"
            aria-label="Heure de début"
            value={p.debut}
            options={OPTIONS_HEURES}
            onChange={(v) => onPlage({ debut: v, fin: p.fin, origine: p.origine })}
            style={{ width: 70 }}
          />
          à
          <Select
            size="small"
            aria-label="Heure de fin"
            value={p.fin}
            options={OPTIONS_HEURES}
            onChange={(v) => onPlage({ debut: p.debut, fin: v, origine: p.origine })}
            style={{ width: 70 }}
          />
        </span>
      </Bloc>
    </div>
  );
}

// Ouvre la modale de logs d'un run (build + traitement) via le store redux.
export function useOuvrirLogs() {
  const redux = useContext(ReactReduxContext);
  return useCallback(
    (r) => {
      if (!redux || !redux.store) return;
      redux.store.dispatch(setSelectedBuild({ build: r.no_build, traitement_id: r.traitement_id }));
      redux.store.dispatch(setShowLogsModal());
    },
    [redux]
  );
}

const C_HEURE = "#e3e8ee";
const C_MINUIT = "#7d8a99";
const C_MINUIT_SIMPLE = "#aab4c0"; // minuit quand la frontiere de jour est ailleurs (cycle)
const C_TRANCHE = "#5b6775";

// Axe en haut sur deux rangees (jours puis heures), dessine dans son propre svg (colle par CSS).
export function dessinerAxe(svg, ech, jours, largeur, origine = 0) {
  const pas = pasGraduation(jours, ech.pxParHeure);
  const { ticks, jours: segs } = graduations(ech, pas, origine);
  svg.selectAll("*").remove();
  svg.attr("width", largeur).attr("height", AXE_H);
  svg.append("rect").attr("width", largeur).attr("height", AXE_H).attr("fill", "#fff");
  svg.append("line").attr("x1", 0).attr("x2", largeur).attr("y1", AXE_H - 0.5).attr("y2", AXE_H - 0.5).attr("stroke", "#c3cbd5");
  svg.append("line").attr("x1", ech.x0).attr("x2", ech.x1).attr("y1", 16.5).attr("y2", 16.5).attr("stroke", C_HEURE);
  segs.forEach((j) => {
    const larg = j.x1 - j.x0;
    const texte = larg >= j.label.length * 5.6 + 6 ? j.label : larg >= j.court.length * 5.6 + 6 ? j.court : null;
    if (texte && larg >= 56) {
      svg.append("text").attr("class", "axe-jour").attr("x", j.x0 + 3).attr("y", 11).attr("font-size", 10).attr("font-weight", 600).attr("fill", "#1d2733").text(texte);
    }
  });
  ticks.forEach((t) => {
    svg.append("line").attr("class", t.frontiere ? "axe-minuit" : t.minuit ? "axe-minuit-simple" : "axe-heure").attr("x1", t.x).attr("x2", t.x).attr("y1", t.frontiere ? 0 : 16).attr("y2", AXE_H).attr("stroke", t.frontiere ? C_MINUIT : t.minuit ? C_MINUIT_SIMPLE : C_HEURE).attr("stroke-width", t.frontiere ? 1.5 : t.minuit ? 1.2 : 1);
    svg.append("text").attr("class", "axe-h").attr("x", t.x + 2).attr("y", 28).attr("font-size", 9).attr("fill", "#6b7785").text(t.label);
  });
  ech.tranches.slice(1).forEach((tr) => {
    const x = tr.x0 - ech.ecart / 2;
    svg.append("line").attr("class", "axe-coupure").attr("x1", x).attr("x2", x).attr("y1", 0).attr("y2", AXE_H).attr("stroke", C_TRANCHE).attr("stroke-width", 2);
  });
  return pas;
}

// Quadrillage vertical sur toute la hauteur : heures fines, minuit plus marque (trait plein),
// petit trait de coupure entre deux tranches de l'axe casse.
export function dessinerGrille(svg, ech, jours, hauteur, origine = 0) {
  const pas = pasGraduation(jours, ech.pxParHeure);
  const { ticks } = graduations(ech, pas, origine);
  const g = svg.append("g").attr("class", "orch-grille");
  ticks.forEach((t) => {
    g.append("line")
      .attr("class", t.frontiere ? "grille-minuit" : t.minuit ? "grille-minuit-simple" : "grille-heure")
      .attr("x1", t.x)
      .attr("x2", t.x)
      .attr("y1", 0)
      .attr("y2", hauteur)
      .attr("stroke", t.frontiere ? C_MINUIT : t.minuit ? C_MINUIT_SIMPLE : C_HEURE)
      .attr("stroke-width", t.frontiere ? 1.5 : t.minuit ? 1.2 : 1);
  });
  ech.tranches.slice(1).forEach((tr) => {
    const x = tr.x0 - ech.ecart / 2;
    g.append("line").attr("class", "grille-coupure").attr("x1", x).attr("x2", x).attr("y1", 0).attr("y2", hauteur).attr("stroke", C_TRANCHE).attr("stroke-width", 2);
  });
}

export function definirHachures(svg, id) {
  svg
    .append("defs")
    .append("pattern")
    .attr("id", id)
    .attr("width", 6)
    .attr("height", 6)
    .attr("patternUnits", "userSpaceOnUse")
    .attr("patternTransform", "rotate(45)")
    .append("rect")
    .attr("width", 2)
    .attr("height", 6)
    .attr("fill", "rgba(255,255,255,.6)");
}

// Barres d'un run (une par tranche traversee), contour par statut, hachures si en cours,
// indicateur "suite" (triangle) sur chaque bord rogne par le filtre d'heures.
export function dessinerBarres(g, segments, { couleur, statut, y, h, titre, idHachures, stale, opacite, libelleStale: texteStale, depuis, jusqua }) {
  // run stale : affiche comme un run en cours (hachures bleues) mais contour orange
  const contour = stale || statut === "warning" ? COULEURS_STATUT.warning : statut === "error" ? COULEURS_STATUT.error : "none";
  segments.forEach((s, i) => {
    const x0 = s.x0;
    const bw = Math.max(3, s.x1 - s.x0);
    const r = g
      .append("rect")
      .attr("class", "barre")
      .attr("x", x0)
      .attr("y", y)
      .attr("width", bw)
      .attr("height", h)
      .attr("rx", 3)
      .attr("fill", couleur)
      .attr("fill-opacity", opacite || null)
      .attr("stroke", contour)
      .attr("stroke-width", 2);
    r.append("title").text(titre);
    if (statut === "running" || stale) {
      g.append("rect")
        .attr("class", stale ? "barre-encours barre-stale" : "barre-encours")
        .attr("x", x0)
        .attr("y", y)
        .attr("width", bw)
        .attr("height", h)
        .attr("rx", 3)
        .attr("fill", `url(#${idHachures})`)
        .attr("stroke", stale ? COULEURS_STATUT.warning : COULEURS_STATUT.running)
        .attr("stroke-width", 2);
    }
    if (depuis && i === 0 && s.tronqueDebut) {
      // run commence avant le debut de la frise : indicateur a gauche
      g.append("text")
        .attr("class", "libelle-depuis")
        .attr("x", x0 + 9)
        .attr("y", y + h / 2)
        .attr("dy", "0.35em")
        .attr("font-size", 10)
        .attr("font-weight", 700)
        .attr("fill", "#1d2733")
        .attr("stroke", "#fff")
        .attr("stroke-width", 3)
        .attr("paint-order", "stroke")
        .text(depuis);
    }
    if (jusqua && i === segments.length - 1 && s.tronqueFin) {
      // run qui se termine apres la fin de la frise : indicateur a droite
      g.append("text")
        .attr("class", "libelle-jusqua")
        .attr("x", x0 + bw - 9)
        .attr("y", y + h / 2)
        .attr("dy", "0.35em")
        .attr("text-anchor", "end")
        .attr("font-size", 10)
        .attr("font-weight", 700)
        .attr("fill", "#1d2733")
        .attr("stroke", "#fff")
        .attr("stroke-width", 3)
        .attr("paint-order", "stroke")
        .text(jusqua);
    }
    if (stale && texteStale && i === segments.length - 1) {
      // libelle a l'extremite droite (sur la barre, halo blanc pour rester lisible)
      g.append("text")
        .attr("class", "libelle-stale")
        .attr("x", x0 + bw - 4)
        .attr("y", y + h / 2)
        .attr("dy", "0.35em")
        .attr("text-anchor", "end")
        .attr("font-size", 10)
        .attr("font-weight", 700)
        .attr("fill", "#b45309")
        .attr("stroke", "#fff")
        .attr("stroke-width", 3)
        .attr("paint-order", "stroke")
        .text(texteStale);
    }
    const mid = y + h / 2;
    const tri = (xa, sens) =>
      g
        .append("path")
        .attr("class", "suite")
        .attr("d", `M${xa},${mid - 4}L${xa + sens * 5},${mid}L${xa},${mid + 4}Z`)
        .attr("fill", "#1d2733");
    if (s.tronqueDebut) tri(x0 + 1, 1);
    if (s.tronqueFin) tri(x0 + bw - 1, -1);
  });
}
